import { useNavigate } from 'react-router-dom';
import { ServerCrash } from 'lucide-react';
import ErrorPageLayout from '@/components/ux/ErrorPageLayout';
import { useAuth } from '@/context/AuthContext';

/**
 * Shown when React could not render the screen.
 *
 * Both buttons clear the error before navigating. They used to only call
 * navigate(), which changed the URL while the error state above the router
 * kept this very page on screen — so "Go back" and "Go to home" both appeared
 * to do nothing at all.
 */
export default function ServerErrorPage({ onReset }) {
  const navigate = useNavigate();
  const { user } = useAuth?.() || {};

  // Route "Home" to the right landing page based on role
  const homeRoute =
    user?.role === 'candidate'   ? '/dashboard'      :
    user?.role === 'super_admin' ? '/super-admin'    :
    user?.role                   ? '/admin/dashboard':
    '/login';

  // Clearing first matters: navigating back to the SAME path would not change
  // the boundary's reset key, and the user would sit on this page having
  // pressed a button that visibly did nothing.
  const clearThen = (action) => () => {
    onReset?.();
    action();
  };

  const goBack = clearThen(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate(homeRoute, { replace: true });
  });

  const goHome = clearThen(() => navigate(homeRoute, { replace: true }));

  return (
    <ErrorPageLayout
      code="500"
      title="Something went wrong"
      description="We hit an unexpected error. Try going back or return to the home page."
      icon={ServerCrash}
      variant="destructive"
      primaryLabel="Go back"
      onPrimary={goBack}
      secondaryLabel="Go to home"
      onSecondary={goHome}
    />
  );
}
